import { ID_COLUMN } from '../csv-import';
import { CsvImportConfig, ImportLookups, ImportOption, ImportValues } from '../csv-import-types';
import { nokSoloOrCompanyOptions, nokStatus, smtpZones } from '../../../defaults/defaults';

/**
 * Nok websites. Mirrors `NokWebsiteImporter` in HQ-API, which is the authority: the rules here
 * only let the grid flag problems early.
 */

const isCompany = (values: ImportValues): boolean => (values.soloOrCompany ?? '').toLowerCase() === 'company';

const options = (choices: { id: string, name: string }[]): ImportOption[] =>
    choices.map((choice: { id: string, name: string }): ImportOption => ({ value: choice.id, label: choice.name }));

const firstId = (lookups: ImportLookups, key: string): string => lookups[key]?.[0]?.value ?? '';

export const nokWebsiteImport: CsvImportConfig = {
    title: 'Nok Websites',
    endpoint: 'hq/nok-import/websites',
    fileName: 'nok-websites',
    columns: [
        ID_COLUMN,
        { key: 'name', label: 'Name', kind: 'text', required: true, maxLength: 255, width: 200 },
        { key: 'status', label: 'Status', kind: 'choice', required: true, choices: options(nokStatus), width: 110 },
        {
            key: 'soloOrCompany', label: 'Solo or Company', kind: 'choice', required: true,
            choices: options(nokSoloOrCompanyOptions), width: 140,
        },
        {
            key: 'url', label: 'Url', kind: 'text', required: isCompany, maxLength: 255, width: 220,
            helper: 'Required for a company. The domain needs to be set up in MailGun for emails to work.',
        },
        {
            key: 'smtpServerZone', label: 'SMTP Zone', kind: 'choice', required: true, choices: options(smtpZones),
            width: 110, helper: 'Must match the MailGun sending server for the domain or sending will fail.',
        },
        { key: 'description', label: 'Description', kind: 'longText', width: 200 },
        {
            key: 'senderDescription', label: 'Sender Description', kind: 'text', required: isCompany, maxLength: 255,
            width: 240, helper: 'Shown to customers choosing a Mystery Shopper sender. Keep it general, never the name or address. Required for a company.',
        },
        {
            key: 'targetIndustries', label: 'Target Industries', kind: 'references', lookup: 'industries', width: 260,
            helper: 'The industries the website shops. Industry ids, separated by semicolons.',
        },
        {
            key: 'industries', label: 'Website Industries', kind: 'references', lookup: 'industries', width: 260,
            helper: "The industries the website's own business is in. Industry ids, separated by semicolons.",
        },
        {
            key: 'nokEmailTemplate', label: 'Email Template', kind: 'reference', lookup: 'templates',
            required: isCompany, width: 240, helper: 'An Email Wrapper template. Required for a company.',
        },
        {
            key: 'nokEmailSignature', label: 'Email Signature', kind: 'reference', lookup: 'signatures',
            required: isCompany, width: 240, helper: 'Required for a company.',
        },
        {
            key: 'productionLogoPath', label: 'Production Logo Path', kind: 'text', maxLength: 255, width: 200,
            helper: 'Needed if the signature shows the Nok logo. Upload the logo itself on the edit screen.',
        },
    ],
    lookups: [
        { key: 'industries', resource: 'industries', label: (record) => record.name + ' (' + record.id + ')' },
        {
            key: 'templates', resource: 'nok_email_templates', filter: { type: 'email_wrapper' },
            label: (record) => record.name + ' (' + record.id + ')',
        },
        { key: 'signatures', resource: 'nok_email_signatures', label: (record) => record.name + ' (' + record.id + ')' },
    ],
    sampleRows: (lookups: ImportLookups): ImportValues[] => [
        {
            name: 'Example Consulting Ltd',
            status: 'active',
            soloOrCompany: 'company',
            url: 'https://example-consulting.co.uk',
            smtpServerZone: 'EU',
            description: 'Procurement consultancy for mid-sized UK firms.\nOffices in Leeds and York.',
            senderDescription: 'A procurement consultancy in the north of England',
            targetIndustries: (lookups.industries ?? []).slice(0, 2).map((option: ImportOption): string => option.value).join(';'),
            industries: firstId(lookups, 'industries'),
            nokEmailTemplate: firstId(lookups, 'templates'),
            nokEmailSignature: firstId(lookups, 'signatures'),
            productionLogoPath: 'assets/logo/logo.png',
        },
        {
            name: 'Jo Example',
            status: 'testing',
            soloOrCompany: 'solo',
            url: '',
            smtpServerZone: 'US',
            description: 'Freelance bookkeeper.',
            senderDescription: '',
            targetIndustries: '',
            industries: '',
            nokEmailTemplate: '',
            nokEmailSignature: '',
            productionLogoPath: '',
        },
    ],
};
